import type { createClient } from "@/lib/supabase/client";
import {
  getLocalPortfolio,
  getLocalWatchlist,
  getLocalWatchlistGroups,
  hasLocalGuestData,
  clearLocalGuestData,
} from "@/lib/local-portfolio/store";

type SupabaseClient = ReturnType<typeof createClient>;

/**
 * Uebernimmt lokal gespeicherte Gast-Watchlist/-Depot-Eintraege in den echten
 * Supabase-Account, sobald sich ein Gast einloggt/registriert - noch nicht von Erik
 * spezifiziert (siehe backlog.md -> "Migrationsfrage"), sinnvolle Annahme: automatisch,
 * ohne Nachfrage, damit keine Gast-Arbeit verloren geht. Best-effort: loescht lokale
 * Daten nur, wenn die Uebernahme vollstaendig ohne Fehler durchlief, sonst bleiben sie
 * (fuer einen erneuten Versuch) unangetastet stehen.
 */
export async function migrateGuestDataToAccount(
  supabase: SupabaseClient,
  userId: string,
): Promise<void> {
  if (!hasLocalGuestData()) return;

  try {
    const symbols = getLocalWatchlist();
    if (symbols.length > 0) {
      const { data: existing, error: readError } = await supabase
        .from("watchlists")
        .select("symbol")
        .eq("user_id", userId);
      if (readError) return;
      const existingSymbols = new Set(((existing ?? []) as { symbol: string }[]).map((r) => r.symbol));
      // Gruppen-Zuordnung (backlog.md -> "Weiterer Nutzwert-Katalog" -> Watchlist) beim
      // Uebernehmen mitnehmen, sonst waere eine als Gast vergebene Kategorisierung nach
      // dem Registrieren spurlos weg.
      const localGroups = getLocalWatchlistGroups();
      const toInsert = symbols
        .filter((s) => !existingSymbols.has(s))
        .map((symbol) => ({ symbol, user_id: userId, group_name: localGroups[symbol] ?? null }));
      if (toInsert.length > 0) {
        const { error: insertError } = await supabase.from("watchlists").insert(toInsert);
        if (insertError) return;
      }
    }

    const positions = getLocalPortfolio();
    if (positions.length > 0) {
      const { data: existingPortfolio, error: portfolioReadError } = await supabase
        .from("portfolios")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();
      if (portfolioReadError) return;

      let portfolioId = (existingPortfolio as { id: string } | null)?.id;
      if (!portfolioId) {
        const { data: created, error: createError } = await supabase
          .from("portfolios")
          .insert({ user_id: userId })
          .select("id")
          .single();
        if (createError || !created) return;
        portfolioId = (created as { id: string }).id;
      }

      const { error: positionsError } = await supabase.from("portfolio_positions").insert(
        positions.map((p) => ({
          portfolio_id: portfolioId,
          symbol: p.symbol,
          quantity: p.quantity,
          purchase_price: p.purchasePrice,
          target_weight: p.targetWeight,
        })),
      );
      if (positionsError) return;
    }

    clearLocalGuestData();
  } catch {
    // Best-effort - lokale Daten bleiben bei jedem Fehler unangetastet stehen.
  }
}
